/**
 * Hotel Name Index – smart layer for fast, accurate search by hotel name variants.
 * Additive only: no API or search flow changes; falls back to existing relevance engine.
 */

import { normalizeHotelName, hotelAliases } from './searchRelevance';

export const MATCH_EXACT = 'EXACT';
export const MATCH_PHRASE = 'PHRASE';
export const MATCH_PREFIX = 'PREFIX';
export const MATCH_TOKEN = 'TOKEN';

// PREFIX > PHRASE so that prefix match is not swallowed when both match (e.g. "sheraton" vs "sheraton grand tel aviv")
const MATCH_PRIORITY = { EXACT: 4, PREFIX: 3, PHRASE: 2, TOKEN: 1 };
const MATCH_SCORE = { EXACT: 0, PHRASE: 20, PREFIX: 40, TOKEN: 60 };

/**
 * Boost values to subtract from relevanceScore (lower = better position).
 */
export const INDEX_MATCH_BOOST = {
  [MATCH_EXACT]: -80,
  [MATCH_PHRASE]: -50,
  [MATCH_PREFIX]: -30,
  [MATCH_TOKEN]: -10,
};

/**
 * Compute stable dedup key for a hotel (same logic as in buildHotelNameIndex).
 * @param {{ ID?: string, Id?: string, DisplayName?: string, Name?: string, GeoLocation?: { Latitude?: number, Longitude?: number } }} hotel
 * @returns {string}
 */
export function getDedupKey(hotel) {
  if (!hotel) return '';
  const hotelId = String(hotel.ID ?? hotel.Id ?? hotel.id ?? '').trim();
  const displayName = (hotel.DisplayName ?? hotel.Name ?? hotel.HotelName ?? '').toString().trim();
  const normalizedName = normalizeHotelName(displayName);

  if (hotelId) return hotelId;

  const lat = hotel.GeoLocation?.Latitude;
  const lon = hotel.GeoLocation?.Longitude;
  if (lat != null && lon != null && !Number.isNaN(Number(lat)) && !Number.isNaN(Number(lon))) {
    const roundedLat = Math.round(Number(lat) * 100) / 100;
    const roundedLon = Math.round(Number(lon) * 100) / 100;
    return (normalizedName || 'n') + '_' + roundedLat + '_' + roundedLon;
  }

  return normalizedName || 'unknown';
}

/**
 * Build index from hotel list. One entry per hotel (keyed by dedupKey).
 * @param {Array<{ ID?: string, Id?: string, DisplayName?: string, Name?: string, GeoLocation?: { Latitude?: number, Longitude?: number } }>} hotels
 * @returns {Record<string, { hotelId: string, dedupKey: string, canonicalName: string, normalizedCanonicalName: string, aliases: string[], normalizedAliases: string[], tokens: string[], tokenSet: Set<string> }>}
 */
export function buildHotelNameIndex(hotels) {
  if (!Array.isArray(hotels) || hotels.length === 0) return {};

  const index = Object.create(null);

  for (const hotel of hotels) {
    const hotelId = String(hotel.ID ?? hotel.Id ?? hotel.id ?? '').trim();
    const displayName = (hotel.DisplayName ?? hotel.Name ?? hotel.HotelName ?? '').toString().trim();
    const normalizedCanonicalName = normalizeHotelName(displayName);

    const dedupKey = getDedupKey(hotel);
    if (!dedupKey || dedupKey === 'unknown') continue;

    const aliasesRaw = hotelAliases[normalizedCanonicalName];
    const normalizedAliases = [normalizedCanonicalName];
    if (Array.isArray(aliasesRaw)) {
      aliasesRaw.forEach((a) => {
        const n = normalizeHotelName(a);
        if (n && !normalizedAliases.includes(n)) normalizedAliases.push(n);
      });
    }

    const allTokens = [];
    normalizedAliases.forEach((s) => {
      s.split(/\s+/).filter(Boolean).forEach((t) => {
        if (!allTokens.includes(t)) allTokens.push(t);
      });
    });
    const tokens = normalizedCanonicalName ? normalizedCanonicalName.split(/\s+/).filter(Boolean) : [];
    const tokenSet = new Set(tokens.length > 0 ? tokens : allTokens.length > 0 ? allTokens : []);

    index[dedupKey] = {
      hotelId: hotelId || dedupKey,
      dedupKey,
      canonicalName: displayName,
      normalizedCanonicalName,
      aliases: aliasesRaw ? [...aliasesRaw] : [],
      normalizedAliases,
      tokens,
      tokenSet,
    };
  }

  return index;
}

/**
 * Search index by query. Returns best match per hotel (by dedupKey).
 * @param {string} query
 * @param {Record<string, { hotelId: string, dedupKey: string, canonicalName: string, normalizedCanonicalName: string, aliases: string[], normalizedAliases: string[], tokens: string[], tokenSet: Set<string> }>} hotelIndex
 * @returns {Map<string, { score: number, matchType: string, matchedValue: string, matchedAlias: string, dedupKey: string, hotelId: string }>} dedupKey -> match info
 */
export function searchHotelNameIndex(query, hotelIndex) {
  const matchesByDedupKey = new Map();

  if (!query || typeof query !== 'string' || !hotelIndex || typeof hotelIndex !== 'object') {
    return matchesByDedupKey;
  }

  const normalizedQuery = normalizeHotelName(query);
  if (!normalizedQuery) return matchesByDedupKey;

  const queryTokens = normalizedQuery.split(/\s+/).filter(Boolean);

  for (const dedupKey of Object.keys(hotelIndex)) {
    const entry = hotelIndex[dedupKey];
    if (!entry) continue;

    const { normalizedCanonicalName, normalizedAliases, tokenSet, hotelId } = entry;
    const searchableStrings = [...new Set([normalizedCanonicalName, ...normalizedAliases])].filter(Boolean);

    let best = null;

    for (const str of searchableStrings) {
      if (!str) continue;

      if (str === normalizedQuery) {
        best = { matchType: MATCH_EXACT, score: MATCH_SCORE.EXACT, matchedValue: str, matchedAlias: str };
        break;
      }
      if (normalizedQuery.length >= 1 && (str.startsWith(normalizedQuery) || normalizedQuery.startsWith(str))) {
        if (!best || MATCH_PRIORITY[MATCH_PREFIX] > MATCH_PRIORITY[best.matchType]) {
          best = { matchType: MATCH_PREFIX, score: MATCH_SCORE.PREFIX, matchedValue: str, matchedAlias: str };
        }
      }
      if (normalizedQuery.length >= 2 && (str.includes(normalizedQuery) || normalizedQuery.includes(str))) {
        if (!best || MATCH_PRIORITY[MATCH_PHRASE] > MATCH_PRIORITY[best.matchType]) {
          best = { matchType: MATCH_PHRASE, score: MATCH_SCORE.PHRASE, matchedValue: str, matchedAlias: str };
        }
      }
    }

    if (queryTokens.length > 0 && queryTokens.some((t) => tokenSet.has(t))) {
      const matchedStr =
        searchableStrings.find((s) => queryTokens.some((t) => s.includes(t))) || normalizedCanonicalName || '';
      if (!best || MATCH_PRIORITY[MATCH_TOKEN] > MATCH_PRIORITY[best.matchType]) {
        best = { matchType: MATCH_TOKEN, score: MATCH_SCORE.TOKEN, matchedValue: matchedStr, matchedAlias: matchedStr };
      }
    }

    if (best) {
      matchesByDedupKey.set(dedupKey, {
        score: best.score,
        matchType: best.matchType,
        matchedValue: best.matchedValue,
        matchedAlias: best.matchedAlias,
        dedupKey,
        hotelId: entry.hotelId,
      });
    }
  }

  return matchesByDedupKey;
}
